// Route-based code-splitting helpers — LOCAL implementation, not a re-export.
//
// The canonical logic lives in `@symbo-ls/workspace-shared/pages/lazyPage.js`
// (full usage docs + the detached-node-race rationale are there). This
// package used to read `_wsShared.pages.X` directly at module scope (auth
// pages) or delegate a whole call through `_wsShared.pages.lazySharedLibPage(
// ...args) : ''` (the /studio, /studio/*, /canvas routes) — both are
// stub-phantom-values under frank's publish serializer: ANY top-level value
// that is a property read off, or the RESULT OF CALLING a function obtained
// through, a stubbed `@symbo-ls/*` import is tainted, even when the loader
// argument is this project's own code (WS-SHARED-LAZYPAGE-HOLLOW-ROUTES-SWITCH-1,
// same defect class fixed for workspace-shell + marketplace; this file closes
// the canvas half, WS-CANVAS-HOLLOW-SHARED-PAGES-BLOCK-PUSH-1). Frank's own
// test proves the fix shape
// (smbls/plugins/frank/__tests__/stubPhantomValueGate.test.js test (ix)): a
// route value must be a LOCAL object literal, defined in this project's own
// source, whose `onCreate` dynamic-`import()`s (or resolves) the shared page
// at RUNTIME — never a property read or a call routed through an external
// stub at module-eval time. So this file carries the real implementation
// locally instead of delegating, mirroring workspace-shell's and
// marketplace's `pages/lazyPage.js` byte-for-byte.
export const canvasFoldSharedLibContext = (root, lib) => {
  const ctx = root.context
  if (lib?.components && ctx?.components) {
    for (const k in lib.components) {
      if (!ctx.components[k]) ctx.components[k] = lib.components[k]
    }
  }
  if (lib?.functions && ctx?.functions) {
    for (const k in lib.functions) {
      if (!ctx.functions[k]) ctx.functions[k] = lib.functions[k]
    }
  }
  if (lib?.state) {
    const seed = {}
    for (const k in lib.state) if (root[k] === undefined) seed[k] = lib.state[k]
    if (Object.keys(seed).length) root.update?.(seed)
  }
}

// THE HOST HOLDS THE MAIN LANDMARK (WS-SHELL-APP-ROUTES-NO-MAIN-LANDMARK-1) —
// the same rule as the shell's pages/lazyPage.js lazyPageUnanchor: `el.set`
// mounts the loaded page INSIDE its placeholder, and when that placeholder is
// the shell's route content (role main + #main-content, workspace context.js
// anchorMainContent) or sits in a PageSlot pane (a host stamped
// `_slotPaneKey`, the labelled region), the page's own `main` tag, `main` role
// and `main-content` id step back instead of nesting a second main landmark
// with a duplicate id. A placeholder that holds no landmark (the standalone
// canvas app's) keeps the page's own.
export const canvasLazyPageUnanchor = (def, host) => {
  if (!def || typeof def !== 'object' || !host) return def
  let held = host.role === 'main'
  for (let p = host.parent, i = 0; !held && p && i < 4; p = p.parent, i++) {
    if (p._slotPaneKey) held = true
  }
  if (!held) return def
  const own = {}
  if (def.tag === 'main') own.tag = 'div'
  if (def.role === 'main') own.role = null
  if (def.id === 'main-content') own.id = null
  if (def.attr && typeof def.attr === 'object' && def.attr.id === 'main-content') {
    own.attr = { ...def.attr, id: null }
  }
  return { ...def, ...own }
}

export const lazyPage = (loader, metadata) => ({
  height: '100%',
  minHeight: '100%',
  metadata,
  // Factory-closure capture (FA205) — `loader` is a parameter of this
  // factory: a closure doesn't survive frank's serialization step, so a
  // published route with no `scope:` here reads a guaranteed-undefined
  // loader and no-ops instead of rendering
  // (WS-CANVAS-LAZYPAGE-SCOPE-LOADER-UNDEFINED-1). `scope: { loader }`
  // travels the value with the emitted page. Read it as `el.scope.loader`
  // explicitly, never a bare `loader()` — `element.scope` is populated by
  // DOMQL's own element constructor for EVERY element with a declared
  // `scope:` (smbls/packages/element/src/create.js, unconditional, dev and
  // published alike), so this is the one form guaranteed to resolve both
  // ways; frank's bare-identifier rewrite to `__scope.loader` is a build-time
  // heuristic that measurably does not fire on every pass over this file.
  scope: { loader },
  onCreate: async (el) => {
    if (el.__lazyLoaded) return
    el.__lazyLoaded = true
    // Late-resolve guard: the loader can resolve after the router (or the
    // AppWindow PageSlot) navigated away and DISPOSED this placeholder —
    // dispose() clears el.__ref (and el.node); never swap then. NOT
    // connected YET is not gone: smbls 3.14.84x builds an app's whole first
    // view detached and attaches it in one insertion (firstRender), and the
    // old `!el.node.isConnected` guard read that as gone and left the route
    // on its skeleton (the workspace shell's signed-out /login, 2026-10-21).
    // el.whenConnected swaps now when connected, else in the task that
    // inserts the node, and never for a disposed element.
    const _place = (def) => {
      if (!el.__ref) return
      if (typeof el.whenConnected === 'function') el.whenConnected(() => el.set(def))
      else el.set(def)
    }
    try {
      const page = await el.scope.loader()
      if (!page) throw new Error('loader resolved empty')
      _place(canvasLazyPageUnanchor({ ...page, height: '100%', minHeight: '100%' }, el))
    } catch (err) {
      console.error('[lazyPage] failed to load route module:', err && err.message)
      _place({
        tag: 'div',
        padding: 'B',
        color: 'caption',
        text: '{{ canvas.lazyPage.loadFailed | polyglot }}'
      })
    }
  }
})

// Shared mount logic for the three lazy-shared-lib-fold routes below — takes
// the loaders' ALREADY-CALLED promises (plain values, no FA205 concern) and
// does the fold/set/fallback that's identical across all three. EXPORTED
// (not a private `_`-prefixed const) so frank resolves the bare call inside
// each factory's onCreate as a project-wide shared helper, the same
// "shared singleton by name" contract `foldSharedLibContext` above already
// relies on — an unexported module-local const is invisible to that
// resolution and reads as a guaranteed-undefined `__scope` miss instead.
export const mountSharedLibPage = async (el, ctxPromise, pagePromise) => {
  // The late-resolve guard of lazyPage above (disposed → never; not
  // attached yet → when it is).
  const _place = (def) => {
    if (!el.__ref) return
    if (typeof el.whenConnected === 'function') el.whenConnected(() => el.set(def))
    else el.set(def)
  }
  try {
    const mod = await ctxPromise
    const lib = mod && mod.default ? mod.default : mod
    const root = el.getRoot?.() || el
    canvasFoldSharedLibContext(root, lib)
    const page = await pagePromise
    if (!page) throw new Error('page loader resolved empty')
    _place(canvasLazyPageUnanchor({ ...page, height: '100%', minHeight: '100%' }, el))
  } catch (err) {
    console.error('[lazySharedLibPage] load failed:', err && err.message)
    _place({
      tag: 'div',
      padding: 'B',
      color: 'caption',
      text: '{{ canvas.lazyPage.loadFailed | polyglot }}'
    })
  }
}

// `/studio`, `/studio/*` and `/canvas` each need their OWN factory (not one
// `lazySharedLibPage(ctxLoader, pageLoader, metadata)` called three times)
// because of FA205 (WS-CANVAS-LAZYPAGE-SCOPE-LOADER-UNDEFINED-1): a `scope:
// {}`-declared value that is ITSELF a dynamic `import()` gets promoted into
// `globalScope` by frank unconditionally (never just tolerated), and any
// unresolved identifier inside a `globalScope` entry is FATAL — this held
// even after giving each route's loader a collision-proof unique name,
// because the trigger is "scope value contains a dynamic import", not name
// collision. studios.js/dashboard.js's own dynamic-import chain leaves
// bundler-internal (`__toESM`/`require_*`) residue once actually analysed
// there (split to FRANK-DYNAMIC-IMPORT-CJS-RESIDUE-1 — pre-existing, only
// unmasked by this fix). The way out: write the `import()` calls directly
// in each route's own `onCreate` instead of threading them through a
// factory parameter — nothing is a free variable any more, so there is
// nothing to declare in `scope:` and nothing for frank to promote. A
// dynamic import inside a plain per-element handler is CHECK-1c debt
// (tolerated, same class as the pre-existing /404 retry and /* catch-all
// findings), never the fatal globalScope class.
export const lazyStudioDashboardPage = (metadata) => ({
  height: '100%',
  minHeight: '100%',
  metadata,
  onCreate: (el) => {
    if (el.__lazyLibPageLoaded) return
    el.__lazyLibPageLoaded = true
    return mountSharedLibPage(
      el,
      Promise.all([import('./studios.js'), import('../functions/index.js')]).then(([m, f]) => ({
        components: m.studiosContext.components,
        functions: { ...f }
      })),
      import('./dashboard.js').then((m) => m.dashboard)
    )
  }
})

export const lazyStudiosBoardPage = (metadata) => ({
  height: '100%',
  minHeight: '100%',
  metadata,
  onCreate: (el) => {
    if (el.__lazyLibPageLoaded) return
    el.__lazyLibPageLoaded = true
    return mountSharedLibPage(
      el,
      Promise.all([import('./studios.js'), import('../functions/index.js')]).then(([m, f]) => ({
        components: m.studiosContext.components,
        functions: { ...f }
      })),
      import('./studios.js').then((m) => m.studios)
    )
  }
})

export const lazyCanvasDashboardPage = (metadata) => ({
  height: '100%',
  minHeight: '100%',
  metadata,
  onCreate: (el) => {
    if (el.__lazyLibPageLoaded) return
    el.__lazyLibPageLoaded = true
    return mountSharedLibPage(
      el,
      Promise.all([import('./studios.js'), import('../functions/index.js')]).then(([m, f]) => ({
        components: m.studiosContext.components,
        functions: { ...f }
      })),
      import('./dashboard.js').then((m) => ({
        ...m.dashboard,
        metadata: { title: 'Canvas / Symbols.app' }
      }))
    )
  }
})
